import React, { useEffect } from "react";

import { RichTextEditorComponent } from "@syncfusion/ej2-react-richtexteditor";
import { RichTextEditor } from "components/forms";
import { debounce } from "lodash";
import { DocumentAnswer } from "store/types";

interface DocumentTextAnswerProps {
  answer?: DocumentAnswer;
  onChange: (value: string) => void;
  onBlur?: (e: React.ChangeEvent) => void;
  onRtlSwitch?: (value: boolean) => void;
  readonly: boolean;
  rteRef?: React.MutableRefObject<RichTextEditorComponent | null>;
  cssClass?: string;
}

export const DocumentTextAnswer: React.FC<DocumentTextAnswerProps> = (props) => {
  const text = (props.answer?.answer.text || "") as string;
  const handleChange = debounce(({ value }: { value: string }) => {
    props.onChange(value);
  }, 1000);

  useEffect(() => {
    if (!props?.rteRef?.current) return;
    if ((props?.rteRef?.current as any)?.id?.includes("document-answer-")) {
      const toolbar = props?.rteRef?.current.element?.querySelector(".e-toolbar-wrapper") as HTMLElement;
      const contentArea = props?.rteRef?.current.element?.querySelector(".e-rte-content") as HTMLElement;

      if (!toolbar || !contentArea) return;

      toolbar.style.opacity = "0";
      toolbar.style.display = "block";
      const toolbarHeight = toolbar.offsetHeight;

      toolbar.style.height = "0px";
      toolbar.style.overflow = "hidden";
      toolbar.style.transition = "height 0.5s ease, opacity 0.5s ease";

      const handleFocus = () => {
        requestAnimationFrame(() => {
          toolbar.style.height = `${toolbarHeight}px`;
          toolbar.style.opacity = "1";
        });
      };

      const handleBlur = (e: FocusEvent) => {
        // Check if the new focus target is still within the editor
        if (!props?.rteRef?.current?.element?.contains(e.relatedTarget as Node)) {
          toolbar.style.height = "0px";
          toolbar.style.opacity = "0";
        }
      };

      contentArea.addEventListener("focus", handleFocus, true);
      contentArea.addEventListener("blur", handleBlur, true);

      // Cleanup
      return () => {
        contentArea.removeEventListener("focus", handleFocus, true);
        contentArea.removeEventListener("blur", handleBlur, true);
      };
    }
  }, []);

  return (
    <RichTextEditor
      id={"document-answer-" + (props.answer?.id || -1)}
      rteRef={props.rteRef}
      change={handleChange}
      readonly={props.readonly}
      isRtl={props.answer?.is_rtl}
      onRtlSwitch={props.onRtlSwitch}
      inlineMode={{ enable: props.readonly }}
      blur={props.onBlur}
      saveInterval={1}
      value={text}
      placeholder="Start writing a text"
      cssClass={props.cssClass}
    />
  );
};
